import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'lib-anc-pagination',
    imports: [CommonModule, FormsModule, NgSelectModule, TranslateModule],
    templateUrl: './anc-pagination.component.html',
    styleUrls: ['./anc-pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncPaginationComponent {
    currentPage = input<number>(1);
    pageSize = input<number>(10);
    totalCount = input<number>(0);
    pageSizeOptions = input<number[]>([10, 20, 50, 100, 200, 500]);
    disabled = input<boolean>(false);

    pageChange = output<number>();
    pageSizeChange = output<number>();

    gotoPageValue = '';
    readonly gotoInputId = `anc-pg-goto-${Math.random()
        .toString(36)
        .slice(2, 9)}`;

    /** Digits only; first digit 1–9 (blocks `0`, `.`, etc.). */
    sanitizeGotoPage(value: string): string {
        return value.match(/^[1-9]\d*/)?.[0] ?? '';
    }

    readonly currentPageNumber = computed(() => {
        const page = Number(this.currentPage());
        return Number.isFinite(page) && page > 0 ? page : 1;
    });

    readonly pageSizeNumber = computed(() => {
        const size = Number(this.pageSize());
        return Number.isFinite(size) && size > 0 ? size : 10;
    });

    readonly totalPages = computed(
        () => Math.ceil(this.totalCount() / this.pageSizeNumber()) || 1
    );

    readonly startRecord = computed(() =>
        this.totalCount() === 0
            ? 0
            : (this.currentPageNumber() - 1) * this.pageSizeNumber() + 1
    );

    readonly endRecord = computed(() =>
        Math.min(
            this.currentPageNumber() * this.pageSizeNumber(),
            this.totalCount()
        )
    );

    /** 5-page sliding window, centered on the current page. */
    readonly visiblePages = computed(() => {
        const maxVisible = 5;
        const total = this.totalPages();
        const current = this.currentPageNumber();
        let start = Math.max(1, current - Math.floor(maxVisible / 2));
        const end = Math.min(total, start + maxVisible - 1);
        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    });

    goToPage(page: number | string): void {
        const nextPage = Number(page);
        const total = this.totalPages();
        const current = this.currentPageNumber();
        if (
            !Number.isFinite(nextPage) ||
            nextPage < 1 ||
            nextPage > total ||
            nextPage === current
        ) {
            return;
        }
        this.pageChange.emit(nextPage);
    }

    submitGotoPage(): void {
        if (this.disabled()) return;
        this.goToPage(this.gotoPageValue);
    }

    goToFirstPage(): void {
        this.goToPage(1);
    }

    goToPreviousPage(): void {
        this.goToPage(this.currentPageNumber() - 1);
    }

    goToNextPage(): void {
        this.goToPage(this.currentPageNumber() + 1);
    }

    goToLastPage(): void {
        this.goToPage(this.totalPages());
    }

    onSizeChange(size: number): void {
        if (this.disabled()) return;
        const next = Number(size);
        if (!Number.isFinite(next) || next <= 0 || next === this.pageSize())
            return;
        this.pageSizeChange.emit(next);
    }
}
